import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import moment from 'moment';
import Header from '../Header';
import './index.scss';
import MetricCard from '../MetricCard';
import { useSelector } from 'react-redux';
import EventFrequency from './EventFrequency';
import { useTranslation } from 'react-i18next';
import TotalDurationRanking from './TotalDurationRanking';
import { ImageHealthLog } from './ImageHealthLog';
import { beeXLogger } from '../../../../helpers/logger';
import { Button, DatePicker } from 'antd';
import { DETECT_EVENT_COMMON } from '../../../../constants/service.types';
import { useMonitoringDataWithInterval } from '../../../../hooks/useMonitoringDataWithInterval';
import { useMonitoringDataFetch } from '../../../../hooks/useMonitoringDataFetch';
import Spinner from '../../../../components/Spinner';
import { EVENT_TYPE } from '../../../../constants/event';
import tamperingCameraImage from '../../../../assets/images/camera_tampering.png';
import { getThumbnailPlaybackByTime } from '../../../../socket/playback';
import { DownloadOutlined } from '@ant-design/icons';
import storageService from '../../../../services/storage.service';
import {
  DECODED_ACCESS_TOKEN,
  NOTIFIES,
} from '../../../../constants/system.keys';
import { openMessage } from '../../../../redux/actions';
import { getExportReportCameraIncidentApi } from '../../../../api/reportApi';
import IMAGE_QUALITY_PLACEHOLDER_IMAGE from '../../../../assets/images/camera_tampering_placeholder_thumbnail.png';

export const IMAGE_QUALITY_WARNING_TYPES = {
  OCCLUDED: 'Occluded',
  NORMAL: 'Normal',
};
const IMAGE_QUALITY_WARNING_TYPES_LABELS = {
  [IMAGE_QUALITY_WARNING_TYPES.OCCLUDED]: 'Occluded',
  [IMAGE_QUALITY_WARNING_TYPES.NORMAL]: 'Normal',
};

const REAL_TIME_TAMPERING = 287;

const ImageQuality = ({
  isActive = true,
  intervalMs = 0,
  onRegisterReload,
  setExportDate,
}) => {
  const [metricsData, setMetricsData] = useState(null);
  const { namespace, detectionEventCommon } = useSelector((state) => state);
  const { t } = useTranslation();
  const [currentPageSize, setCurrentPageSize] = useState(3);
  const [metricsLoadingState, setMetricsLoadingState] = useState({});
  const childReloadRefs = useRef({});
  const isMountedRef = useRef(true);
  const [selectedDate, setSelectedDate] = useState(moment());

  useEffect(() => {
    if (!isActive) {
      setCurrentPageSize(3);
    }
  }, [isActive]);

  const registerChildReload = useCallback((childKey, reloadFn) => {
    childReloadRefs.current[childKey] = reloadFn;
  }, []);

  const baseFetchData = useMonitoringDataFetch(REAL_TIME_TAMPERING);

  const transformMonitoringData = (rawData) => {
    return rawData.map((item) => {
      const startTime = moment(item.startTime);
      let durationStr = null;

      if (item.endTime > 0) {
        const endTime = moment(item.endTime);
        const duration = moment.duration(endTime.diff(startTime));

        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        const durationParts = [];
        if (hours > 0) durationParts.push(`${hours}h`);
        if (minutes > 0) durationParts.push(`${minutes}m`);
        if (seconds > 0 || durationParts.length === 0)
          durationParts.push(`${seconds}s`);

        durationStr = durationParts.join(' ');
      }
      const rate = parseInt(item.rate, 10);
      const status = durationStr
        ? IMAGE_QUALITY_WARNING_TYPES.NORMAL
        : IMAGE_QUALITY_WARNING_TYPES.OCCLUDED;
      const type = rate < 70 ? 'tertiary' : 'error';

      return {
        ...item,
        image: item?.image || IMAGE_QUALITY_PLACEHOLDER_IMAGE,
        duration: durationStr || -1,
        status,
        type,
      };
    });
  };

  const fetchMonitoringDataCallback = useCallback(async () => {
    if (!namespace) return null;

    try {
      const rawData = await baseFetchData();
      console.log('🚀 ~ ImageQuality ~ rawData:', rawData);

      const transformedData = transformMonitoringData(rawData);

      const sortedData = transformedData.sort(
        (a, b) => moment(b.startTime) - moment(a.startTime),
      );

      if (isMountedRef.current) {
        setMetricsData(sortedData);
      }
      console.log('🚀 ~ ImageQuality ~ sortedData:', sortedData);
      sortedData.forEach((item, index) => {
        if (!item?.serial) return;
        console.log("🚀 ~ ImageQuality ~ item:", item)
        beeXLogger.debug('ImageQuality - Get thumbnail for: ', {
          serial: item.serial,
          queryTime: moment(item.startTime * 1000).format('YYYY-MM-DD HH:mm:ss'),
          namespace,
        });
        getThumbnailPlaybackByTime({
          serial: item.serial,
          queryTime: moment(item.startTime * 1000).format('YYYY-MM-DD HH:mm:ss'),
          namespace,
        })
          .then((res) => {
            beeXLogger.debug('ImageQuality - getPlaybackThumbnail - res: ', res);
            if (!isMountedRef.current) return;
            setMetricsData((prev) =>
              prev.map((d, i) =>
                i === index ? { ...d, image: res?.imageBase64 ?? d.image } : d,
              ),
            );
          })
          .catch((err) => {
            beeXLogger.debug('ImageQuality - getPlaybackThumbnail - err: ', err);
          });
      });

      return sortedData;
    } catch (err) {
      beeXLogger.error(err);
      throw err;
    }
  }, [baseFetchData, namespace]);

  const { reload: mainReload } = useMonitoringDataWithInterval(
    fetchMonitoringDataCallback,
    isActive ? intervalMs : 0,
    [namespace, isActive],
  );

  const handleReload = useCallback(() => {
    mainReload();
    setMetricsData([]);
    Object.values(childReloadRefs.current).forEach((reloadFn) => {
      if (typeof reloadFn === 'function') {
        reloadFn();
      }
    });
  }, [mainReload]);

  useEffect(() => {
    if (onRegisterReload && typeof onRegisterReload === 'function') {
      onRegisterReload(handleReload);
    }
  }, [handleReload, onRegisterReload]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const latestEvent = detectionEventCommon?.[0];

    beeXLogger.debug('🚀 ~ ImageQuality ~ latestEvent:', latestEvent);
    if (
      latestEvent?.serviceId === EVENT_TYPE.TAMPERED_CAMERA &&
      isActive &&
      namespace &&
      intervalMs === 0
    ) {
      setTimeout(() => fetchMonitoringDataCallback(), 1000);
    }
  }, [
    detectionEventCommon,
    isActive,
    namespace,
    intervalMs,
    fetchMonitoringDataCallback,
  ]);
  return (
    <div className="image-quality-container">
      <Header
        title={t('Real-time detection of blurred or occluded cameras.')}
        chipLabel={t('Live Analysis')}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBlock: 8,
          gap: 8,
        }}
      >
        <DatePicker
          className="picker"
          format="DD-MM-YYYY"
          placeholder={t('Select date')}
          value={selectedDate}
          style={{
            width: '255px',
            marginLeft: '20px',
            height: '32px',
          }}
          onChange={(value) => {
            setSelectedDate(value);
            if (setExportDate) {
              setExportDate(value);
            }
          }}
        />
      </div>
      <div className="image-quality-content">
        <div className="image-quality-metric-card-list">
          {metricsData
            ? metricsData.slice(0, currentPageSize).map((metric) => (
                <div
                  className={`image-quality-metric-card-item ${metric.status === IMAGE_QUALITY_WARNING_TYPES.OCCLUDED ? 'occluded' : 'normal'}`}
                  key={metric.serial}
                >
                  {metricsLoadingState[metric.serial] ? (
                    <Spinner />
                  ) : (
                    <MetricCard
                      header={
                        <ImageQualityCardHeader
                          image={metric?.image || tamperingCameraImage}
                          status={metric.status}
                        />
                      }
                      body={<ImageQualityCardBody text={metric.name} />}
                      footer={
                        metric?.status ===
                        IMAGE_QUALITY_WARNING_TYPES.OCCLUDED ? (
                          <ImageQualityCardFooter
                            text={
                              metric.duration === -1
                                ? `Status: Obstructed - Detection Rate: ${metric.rate}%`
                                : `Duration: ${metric.duration} - Detection Rate: ${metric.rate}%`
                            }
                            status={metric.status}
                          />
                        ) : (
                          <ImageQualityCardFooter
                            text={`Last Obstruction: ${moment(
                              metric.startTime,
                            ).fromNow()} – Detection Rate: ${metric.rate}%`}
                            status={metric.status}
                          />
                        )
                      }
                    />
                  )}
                </div>
              ))
            : Array.from({ length: currentPageSize || 3 }).map((_, idx) => (
                <div
                  className="image-quality-metric-card-item"
                  key={`skeleton-${idx}`}
                >
                  <Spinner />
                </div>
              ))}
        </div>
        {metricsData?.length > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 16,
            }}
          >
            <Button
              type="primary"
              onClick={() =>
                setCurrentPageSize((prev) =>
                  metricsData?.length >= prev ? prev + 3 : prev,
                )
              }
            >
              {t('Load more')}
            </Button>
          </div>
        )}
        <div className="image-quality-section image-quality-section-flex">
          <div className="image-quality-section-flex-item">
            <EventFrequency
              isActive={isActive}
              intervalMs={intervalMs}
              onRegisterReload={(fn) =>
                registerChildReload('eventFrequency', fn)
              }
              selectedDate={selectedDate}
            />
          </div>
          <div className="image-quality-section-flex-item">
            <TotalDurationRanking
              isActive={isActive}
              intervalMs={intervalMs}
              onRegisterReload={(fn) =>
                registerChildReload('totalDuration', fn)
              }
              selectedDate={selectedDate}
            />
          </div>
        </div>
        <div className="image-quality-section">
          <div className="image-quality-section-item">
            <ImageHealthLog
              isActive={isActive}
              intervalMs={intervalMs}
              onRegisterReload={(fn) =>
                registerChildReload('imageHealthLog', fn)
              }
              selectedDate={selectedDate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageQuality;

export const ImageQualityCardHeader = ({ image = '', status = '' }) => {
  const statusClass = useMemo(() => {
    switch (status) {
      case IMAGE_QUALITY_WARNING_TYPES.OCCLUDED:
        return 'error';
      case IMAGE_QUALITY_WARNING_TYPES.NORMAL:
        return 'ongoing';
      default:
        return '';
    }
  }, [status]);

  return (
    <div className="image-quality-card-header">
      <div className="image-quality-card-header-left">
        <img className="image-quality-card-image" src={image} alt="Card" />
      </div>
      <div className="image-quality-card-header-right">
        <div className={`image-quality-status ${statusClass}`}>
          {IMAGE_QUALITY_WARNING_TYPES_LABELS[status]}
        </div>
      </div>
    </div>
  );
};

export const ImageQualityCardBody = ({ text }) => {
  return (
    <div className="image-quality-card-body">
      <p>{text}</p>
    </div>
  );
};

export const ImageQualityCardFooter = ({ text, status = '' }) => {
  const statusClass = useMemo(() => {
    switch (status) {
      case IMAGE_QUALITY_WARNING_TYPES.OCCLUDED:
        return 'error';
      case IMAGE_QUALITY_WARNING_TYPES.NORMAL:
        return 'normal';
      default:
        return '';
    }
  }, [status]);

  return (
    <div className={`image-quality-card-footer ${statusClass}`}>
      <p>{text}</p>
    </div>
  );
};
